import lodashMerge from 'lodash.merge';
import resourcesWithEmailTemplates from '@constants/resourcesWithEmailTemplates';
import appConfig from '@configs/appConfig';

const englishMessages = {
  ra: {
    action: {
      add_filter: 'Add filter',
      add: 'Add',
      back: 'Go Back',
      bulk_actions: '1 item selected |||| {{smart_count}} items selected',
      cancel: 'Cancel',
      clear_input_value: 'Clear value',
      clone: 'Clone',
      confirm: 'Confirm',
      create: 'Create',
      create_item: 'Create {{item}}',
      delete: 'Delete',
      edit: 'Edit',
      export: 'Export',
      list: 'List',
      refresh: 'Refresh',
      remove_filter: 'Remove this filter',
      remove: 'Remove',
      save: 'Save',
      search: 'Search',
      select_all: 'Select all',
      select_row: 'Select this row',
      show: 'Show',
      sort: 'Sort',
      undo: 'Undo',
      unselect: 'Unselect',
      expand: 'Expand',
      close: 'Close',
      open_menu: 'Open menu',
      close_menu: 'Close menu',
      update: 'Update',
      move_up: 'Move up',
      move_down: 'Move down',
    },
    boolean: {
      true: 'Yes',
      false: 'No',
      null: ' ',
    },
    page: {
      create: 'Create {{name}}',
      dashboard: 'Dashboard',
      edit: '{{name}} #{{id}}',
      error: 'Something went wrong',
      list: '{{name}}',
      loading: 'Loading',
      not_found: 'Not Found',
      show: '{{name}} #{{id}}',
      empty: 'No {{name}} yet.',
      invite: 'Do you want to add one?',
    },
    input: {
      file: {
        upload_several: 'Drop some files to upload, or click to select one.',
        upload_single: 'Drop a file to upload, or click to select it.',
      },
      image: {
        upload_several: 'Drop some pictures to upload, or click to select one.',
        upload_single: 'Drop a picture to upload, or click to select it.',
      },
      references: {
        all_missing: 'Unable to find references data.',
        many_missing:
          'At least one of the associated references no longer appears to be available.',
        single_missing:
          'Associated reference no longer appears to be available.',
      },
      password: {
        toggle_visible: 'Hide password',
        toggle_hidden: 'Show password',
      },
    },
    message: {
      about: 'About',
      are_you_sure: 'Are you sure?',
      bulk_delete_content:
        'Are you sure you want to delete this {{name}}? |||| Are you sure you want to delete these {{smart_count}} items?',
      bulk_delete_title: 'Delete {{name}} |||| Delete {{smart_count}} {{name}}',
      bulk_update_content:
        'Are you sure you want to update this {{name}}? |||| Are you sure you want to update these {{smart_count}} items?',
      bulk_update_title: 'Update {{name}} |||| Update {{smart_count}} {{name}}',
      delete_content: 'Are you sure you want to delete this item?',
      delete_title: 'Delete {{name}} #{{id}}',
      details: 'Details',
      error: "A client error occurred and your request couldn't be completed.",
      invalid_form: 'The form is not valid. Please check for errors',
      loading: 'The page is loading, just a moment please',
      no: 'No',
      not_found: 'Either you typed a wrong URL, or you followed a bad link.',
      yes: 'Yes',
      unsaved_changes:
        "Some of your changes weren't saved. Are you sure you want to ignore them?",
    },
    navigation: {
      no_results: 'No results found',
      no_more_results:
        'The page number {{page}} is out of boundaries. Try the previous page.',
      page_out_of_boundaries: 'Page number {{page}} out of boundaries',
      page_out_from_end: 'Cannot go after last page',
      page_out_from_begin: 'Cannot go before page 1',
      page_range_info: '{{offsetBegin}}-{{offsetEnd}} of {{total}}',
      page_rows_per_page: 'Rows per page:',
      next: 'Next',
      prev: 'Prev',
      skip_nav: 'Skip to content',
    },
    sort: {
      sort_by: 'Sort by {{field}} {{order}}',
      ASC: 'ascending',
      DESC: 'descending',
    },
    auth: {
      auth_check_error: 'Please login to continue',
      user_menu: 'Profile',
      username: 'Username',
      password: 'Password',
      sign_in: 'Sign in',
      sign_in_error: 'Authentication failed, please retry',
      logout: 'Logout',
    },
    notification: {
      updated: 'Element updated |||| {{smart_count}} elements updated',
      created: 'Element created',
      deleted: 'Element deleted |||| {{smart_count}} elements deleted',
      bad_item: 'Incorrect element',
      item_doesnt_exist: 'Element does not exist',
      http_error: 'Server communication error',
      data_provider_error: 'dataProvider error. Check the console for details.',
      i18n_error: 'Cannot load the translations for the specified language',
      canceled: 'Action cancelled',
      logged_out: 'Your session has ended, please reconnect.',
      not_authorized: "You're not authorized to access this resource.",
    },
    validation: {
      required: 'Required',
      minLength: 'Must be {{min}} characters at least',
      maxLength: 'Must be {{max}} characters or less',
      minValue: 'Must be at least {{min}}',
      maxValue: 'Must be {{max}} or less',
      number: 'Must be a number',
      email: 'Must be a valid email',
      oneOf: 'Must be one of: {{options}}',
      regex: 'Must match a specific format (regexp): {{pattern}}',
    },
  },
};
const translates = {
  resources: {
    passwords: {
      fields: {
        name: 'Name',
        link: 'Access link',
        date: 'Expiration Date',
      },
      deleteDialog: {
        caption: 'Delete one time password record?',
        text:
          'This action will delete the selected Notification Group and it can not be undone.',
      },
    },
    tenantStatistic: {
      name: 'Tenant Statistic',
      tenant: 'Tenant',
      hooksUsage: 'Hooks usage',
      storageUsage: 'Storage usage',
      apiUsage: 'External API calls',
      hookName: 'Name',
      hookTotal: 'Total',
      hookProcessed: 'Processed',
      totalHooks: 'Total hooks received',
      successHooks: 'Successfully processed',
      storageLetters: 'Letters container',
      storageUserImages: 'User images container',
      apiTotal: 'Total API calls',
      apiGeneric: 'Generic integrations',
      apiMeraki: 'Meraki integration',
      apiSophos: 'Sophos integration',
    },
    escalationRules: {
      fields: {
        id: 'ID',
        name: 'Name',
        isActive: 'Enabled',
        smsEnabled: 'SMS',
        emailEnabled: 'Email',
      },
      deleteCaption: 'Delete current escalation rule?',
      deleteText:
        'This action will delete current escalation rule and it can not be undone.',
    },
    webHookResults: {
      name: 'Webhook results',
      fields: {
        id: 'ID',
        date: 'Date',
        status: 'Status',
        errorMessage: 'Error',
      },
    },
    triggers: {
      name: 'Triggers',
      deleteCaption: 'Delete current trigger?',
      deleteText:
        'This action will delete current trigger with incidents and it can not be undone.',
      fields: {
        name: 'Trigger Name',
        eventMessage: 'Event message',
        tags: 'Tags',
      },
      switchDescription:
        '<p style="color: black; font-size: 16px; text-align: justify">' +
        `When this is turned on the system will only generate a new incident if the subject is unique. If an incident already exists with the same subject ${appConfig.projectName} assumes it is the same alert and will not create a new incident, rather it will update the existing one with the same subject.  \n` +
        '</p><p style="color: black; font-size: 16px; text-align: justify">' +
        'If this option is turned off the system will generate a new incident for each email that matches the trigger regardless of the subject. This could lead to duplicate incidents if the same alert comes through multiple times. ' +
        '</p>',
    },
    documents: {
      name: 'Documents',
    },
    incidents: {
      name: 'Incidents',
      fields: {
        triggerName: 'Trigger',
        parserName: 'Parser',
        webHookName: 'WebHook',
        sourceName: 'Source',
        dateTriggered: 'Date Triggered',
        assigneeName: 'Assignee Name',
        actions: 'Actions',
        emailSubject: 'Subject',
        tags: 'Tags',
        incidentStatus: 'Status',
        lastEscalationDate: 'Escalation',
      },
      deleteDialog: {
        deleteCaption: 'Delete Notification Group?',
        deleteText:
          'This action will delete the selected Notification Group and it can not be undone.',
      },
    },
    notificationGroups: {
      name: 'Notification groups',
      deleteCaption: 'Delete Notification Group?',
      deleteText:
        'This action will delete the selected Notification Group and it can not be undone.',
      deleteUserCaption: 'Delete user from group?',
      deleteUserText:
        'This action will delete current user from group and it can not be undone.',
      fields: {
        name: 'Group Name',
        userName: 'User Name',
        userEmail: 'User Email',
        userPhone: 'User Phone',
        source: 'Source',
      },
    },
    autotaskHooks: {
      name: 'AT Hooks',
    },
    connectWiseHooks: {
      name: 'CW Hooks',
    },
    billingLinks: {
      name: 'Billing links',
      fields: {
        name: 'Name',
        link: 'LinkD',
        tenantName: 'Tenant Name',
        expirationDateUtc: 'Expiration date',
      },
      deleteDialog: {
        caption: 'Delete billing API key',
        text: 'This action will delete current tile and it can not be undone.',
      },
    },
    crmUsers: {
      name: 'PSA Users',
      fields: {
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
      },
    },
    crmTickets: {
      name: 'PSA Tickets',
      fields: {
        creationDate: 'Date',
        ticketId: 'Ticket ID',
        mailBoxName: 'MailBox',
        ticketStatus: 'Status',
        subject: 'Subject',
        body: 'Body',
        errorMessage: 'Errors',
      },
    },
    dashboard: {
      name: 'Dashboard',
      createDialog: {
        titleCreate: 'Create new dashboard',
        titleUpdate: 'Update dashboard name',
        content: 'Create content',
        label: 'Dashboard name',
      },
      header: {
        addDashboard: 'Add dashboard',
        editDashboard: 'Edit dashboard',
        AddTile: 'Add tile',
        DeleteDashboard: 'Delete Dashboard',
        deleteDialogCaption: 'Remove current dashboard?',
        deleteDialogText:
          'This action will delete current dashboard and it can not be undone.',
        fullScreen: 'Full screen',
      },
      tile: {
        deleteDialogCaption: 'Remove this tile?',
        deleteDialogText:
          'This action will delete current tile and it can not be undone.',
      },
      tileForm: {
        headerTextCreate: 'Create tile content',
        headerTextUpdate: 'Update tile content',
        nameLabel: 'Tile name',
        typeLabel: 'Tile type',
        dataViewLabel: 'Data View',
        accordionLabel: 'Data View Filters',
        groupingLabel: 'Group by',
      },
    },
    alertTemplates: {
      name: 'Templates',
      fields: {
        name: 'Name',
        allAdmins: 'Admins',
        snmp: 'SNMP',
        emailsTemplateBehaviour: 'Emails',
        hooksTemplateBehaviour: 'Hooks',
        emails: 'Emails',
        httpServerIds: 'Slected hooks',
        lastApplyDateTime: 'Last apply',
        wasChangedAfterApply: 'Was changed',
        lastApplyUser: 'Apply user',
      },
    },
    webHooks: {
      name: 'Web Hooks',
      fields: {
        integrationName: 'Integration Name',
        integrationGuid: 'Integration Guid',
      },
      modal: {
        deleteTitle: 'Delete current hook settings',
        deleteContent:
          'This operation will remove current hook settings. It can not be undone',
      },
    },
    smsLogs: {
      name: 'SMS Logs',
      fields: {
        dateTime: 'Date',
        to: 'Recipient',
        message: 'Message',
        status: 'Status',
        errorMessage: 'Error message',
      },
    },
    parsingStatus: {
      name: 'Parsing status',
      columns: {
        attentionRequired: 'Attention',
        failed: 'Failed',
        ignored: 'Ignored',
        name: 'Box Name',
        parsersCount: 'Parsers',
        processed: 'Processed',
        quotaExceeded: 'Quota',
        tenantName: 'Tenant',
        total: 'Total',
      },
    },
    customFields: {
      name: 'Custom Fields',
    },
    ftpConnections: {
      name: 'FTP Connections',
      fields: {
        url: 'FTP address',
        name: 'Name',
        userName: 'User Name',
      },
      deleteDialog: {
        caption: 'Are you sure to delete this connection',
        text: 'This operation can not be undone.',
      },
    },
    apiKeys: {
      name: 'Api Keys',
      fields: {
        id: 'ID',
        key: 'Api Key',
        name: 'Name',
        date: 'Expiration date',
      },
      deleteDialog: {
        caption: 'Are you sure to delete this key?',
        text:
          'Deleting this key will lead to revoking all access rights for the key handlers',
      },
      dialogs: {
        copied: 'Copied to the clipboard',
      },
    },
    localCompany: {
      name: 'Company Mapping',
      form: {
        inputLabel: 'Local Company',
      },
      importFromDataSources: 'Import from Data Sources',
      importFromDataViews: 'Import from Data Views',
    },
    exporting: {
      download: {
        title: 'Download',
        header:
          'Download parsed data as Microsoft Excel (XLSX), Comma Separated Values (CSV) or JSON.',
        checkBoxLabel:
          'Include system information (like Id, acknowledge, assignment, etc...)',
      },
      googleSheets: {
        title: 'Google sheets',
        header: 'You can open parser results with Google Sheet',
        text: 'Just copy the following link to a cell of your',
        limitationCaption: 'Limitations using those formulas:',
        limit1:
          'Deleting a document from your mailbox will also remove it from your Google Sheet',
        limit2:
          'Google Sheets will sync the content of your sheet with your data source every hour or so',
      },
      webHooks: {
        title: 'WebHooks',
        caption: 'WebHooks shifted to the notifications region',
        body: 'To use webHooks as a trigger for a new event, follow the ',
      },
      ftp: {
        title: 'FTP Upload',
        header: 'Select FTP connection and select format for uploading',
      },
    },
    tenantSettings: {
      name: 'Tenant Settings',
    },
    customPalette: {
      name: 'Custom palette',
    },
    notificationSettings: {
      name: 'Notifications',
      emptyFilterTitle: 'No filters detected',
      emptyFilterContent:
        'Do you really want to save notification without filters? In this case all new data will trigger notifications',
      deleteTitle: 'Delete current ticketing settings',
      deleteContent:
        'This operation will remove current ticketing settings. It can not be undone',
      ticketSettings: {
        openTicketText: 'Creates a new ticket using selected settings',
        closeTicketText: 'Closes an existing open tickets',
      },
      useGeneric: 'Use generic pipeline',
    },
    integrations: {
      name: 'PSA Integrations',
      update: {
        deleteTitle: 'Delete current integration',
        deleteContent:
          'This operation will remove current integration. It can not be undone',
        ticketDefault: {
          deleteTitle: 'Delete current ticket default',
          deleteContent:
            'This operation will remove current ticket default. It can not be undone',
        },
      },
      create: {
        addBtn: 'Add PSA',
      },
    },
    buttons: {
      ok: 'OK',
      skip: 'Skip',
      create: 'Create',
      cancel: 'Cancel',
      submit: 'Submit',
      deleteAccount: 'Delete Account',
      confirm: 'Confirm',
      send: 'Send',
      test: 'Test',
      back: 'Back',
      saveAs: 'Save As',
      resetPwd: 'Reset password',
      change: 'Change password',
      forgot: 'Forgot password',
      gotIt: 'Got It',
      save: 'Save',
      reset: 'Reset',
      apply: 'Apply',
      setDefault: 'Defaults',
    },
    types: {
      name: 'Custom Types',
      screenHeader: "Edit types, don't forget to customize them",
      createTypeDialog: {
        title: 'Add new custom type',
        content:
          'To indicate data after mail messages parsing please describe your custom types. Name should be short and understandable.',
        label: 'Type name',
      },
      editTypeDialog: {
        title: 'Edit custom type',
        content:
          'To indicate data after mail messages parsing please describe your custom types. Name should be short and understandable.',
        label: 'Type name',
      },
      createTypeValueDialog: {
        title: 'Add new type value',
        title2: 'Edit type value',
        content:
          'To indicate data after mail messages parsing please describe your custom type values. Name should be short and understandable.',
        label1: 'Type Value Name',
        label2: 'Identity values',
        recordCreated: 'Record created',
        recordUpdated: 'Record updated',
        recordExists: 'Same name of value already exists in the array',
      },
      unableToRemove: 'Unable to remove type',
      confirmDeleteType: {
        title: 'Delete type',
        content: 'This operation will remove type and all type values.',
      },
      confirmDeleteTypeValue: {
        title: 'Delete type value',
        content: 'This operation will remove type and all type values.',
      },
    },
    home: {
      name: 'Mail Boxes',
      mailBoxCard: {
        dashboard: 'Dashboard',
        parsed: 'AttentionRequired',
        document: 'Document',
        documents: 'Documents',
        processed: 'Processed',
        failed: 'Failed',
        quotaExceeded: 'Quota exceeded',
        ignored: 'Ignored',
        parsers: 'Parsers',
        parser: 'Parser',
        export: 'Export',
        settings: 'Settings',
      },
      mailBoxCardCreate: {
        create: 'Create new mailbox',
        name: 'Mailbox name',
        text: 'Enter a name for your mailbox, then click Create',
        button: 'Create',
        error: 'Error during mail box creation',
      },
    },
    mailBox: {
      layout: {
        dashboard: 'Dashboard',
        documents: 'Documents',
        parsers: 'Parsers',
        settings: 'Settings',
        ignored: 'Ignored',
      },
      dashboard: {
        attentionRequired: 'Attention Required',
        processed: 'Processed',
        failed: 'Failed',
        quotaExceeded: 'Quota exceeded',
        unrecognized: 'Unrecognized',
      },
      documents: {
        subject: 'Subject',
        status: 'Status',
        dateTime: 'Received date',
        deleteLetterCaption: 'Deleting the letter',
        deleteLetterText:
          'Are you sure to remove the letter. This action will not affect total statistic',
        unableRemoveLetter: 'Unable to remove record',
        sender: 'Sender',
        reParseQueued: 'Message reParser queued',
        reParserFailed: 'Message reParser failed',
        actions: 'Actions',
        parsedDate: 'Last Parsed Date',
        bulkActions: {
          deleteSelected: 'Delete selected',
          reparse: 'Re-Parse',
          ignore: 'Ignore',
          reprocess: {
            title: 'Reprocess selected emails',
            content: 'Are you sure you want to reprocess selected emails?',
          },
        },
      },
      settings: {
        ignoreSpoiler: {
          header: 'Ignore messages',
          secondHeader:
            'Use this setting to ignore messages for current mailbox',
        },
        ignore: {
          leftText:
            'Activate this setting to provide ignoring messages based on logical rules',
          checkBoxLabel: 'Enable message ignored rules',
          button: 'Save',
        },
      },
    },
    letter: {
      from: 'From',
      to: 'To',
      received: 'Received',
      processed: 'Processed',
      template: 'Parser',
      subject: 'Subject',
      body: 'Body',
      html: 'HTML',
      text: 'Text',
      headers: 'Headers',
      json: 'JSON',
      back: 'Back',
      status: 'Status',
      reparseMessage: 'Reprocess email',
      reparseMessages: 'Reprocess all',
      reParseQueued: 'Message re-parse queued',
      reParserFailed: 'Message re-parse failed',
    },
    role: {
      name: 'Roles',
      userAddSuccess: 'User added successfully',
      userAddError: 'Current user already added to roles',
      userRemoved: 'User successfully removed from current role',
      role: 'role',
      users: 'users',
      editRole: 'Edit Role "{{name}}"',
      fields: {
        id: 'ID',
        name: 'Name',
        updatedAt: 'Updated At',
        permissions: 'Permissions',
        user: 'User',
        firstName: 'First Name',
        lastName: 'Last Name',
      },
    },
    data: {
      name: 'Data',
      acknowledgeDialog: {
        label: 'keep acknowledged',
        infobox: 'By default new result will reset acknowledgement',
      },
      bulkActions: {
        acknowledgeSelected: 'Acknowledge selected',
        assignSelected: 'Assign selected',
        deleteSelected: 'Delete selected',
      },
    },
    admin: {
      name: 'Admin',
    },
    coupons: {
      name: 'Coupons',
      fields: {
        couponCode: 'Coupon Code',
        type: 'Type',
        duration: 'Duration (months)',
        tenantAlias: 'Tenant Alias',
      },
      deleteDialog: {
        caption: 'Are you sure to delete this coupon?',
        text: 'This operation can not be undone.',
      },
    },
    demoReferralLinks: {
      name: 'Demo Links',
      fields: {
        name: 'Link Name',
        link: 'Link',
        consumer: 'Consumed by',
      },
      deleteDialog: {
        caption: 'Are you sure to delete this link',
        text: 'This operation can not be undone.',
      },
    },
    subscriptions: {
      name: 'Subscriptions',
      paymentFailed: 'Payment failed. Try again',
      paymentSuccess: 'Payment success',
      subscriptionName: 'Subscription name',
      interval: 'Interval',
      intervalCount: 'Interval Count',
      quantity: 'Quantity',
      pricePerUnit: 'Price Per Unit',
      amountOfSubscriptions: 'Amount Of subscriptions',
      payOrder: 'Pay Order',
      stripePaymentForm: 'Stripe Payment Form',
      totalSubscriptionPrice: 'Total Subscription Price',
      fields: {
        id: 'ID',
        tenantName: 'Tenant Name',
        tenantAdmin: 'Tenant Admins',
        active: 'Is Active',
        stripeId: 'Stripe Id',
        information: 'Information',
        name: 'Price plan',
        status: 'Status',
        nextPayment: 'Next payment',
        lastSyncDate: 'Last Stripe Sync',
        email: 'Alerts',
        sms: 'SMS',
        devices: 'Monitors',
        isDemo: 'Is Demo',
        code: 'Code',
        quantity: 'Quantity',
      },
    },
    tenants: {
      name: 'Tenants',
      fields: {
        id: 'ID',
        tenantName: 'Tenant Name',
        usersCount: 'Users count',
        tenantAdmin: 'Tenant Admin',
        tenantAlias: 'Tenant Alias',
      },
    },
    deviceMonitors: {
      name: 'Uptime Monitors',
      bulkActions: {
        deleteSelected: 'Delete selected',
        updateRunningState: 'Update running state',
        reprocess: {
          title: 'Update running state selected device monitors',
          content:
            'Are you sure you want to Update running state selected device monitors',
        },
      },
      deviceThreshold: {
        modal: {
          deleteTitle: 'Delete current Device Threshold',
          deleteContent:
            'This operation will remove current Device Threshold. It can not be undone',
        },
      },
      deviceAlerts: {
        modal: {
          deleteTitle: 'Delete current Device Alert',
          deleteContent:
            'This operation will remove current Device Alert. It can not be undone',
        },
      },
      modal: {
        deleteTitle: 'Delete current Device Monitor',
        deleteContent:
          'This operation will remove current Device Monitor. It can not be undone',
      },
    },
    roadmap: {
      name: 'Roadmap',
    },
    dataViews: {
      name: 'Data Views',
      fields: {
        id: 'ID',
        name: 'Name',
        data: 'Data Container',
        tenantName: 'Tenant Name',
      },
      form: {
        nameLabel: 'Data View Name',
      },
    },
    mailBoxes: {
      name: 'Mail Boxes',
      fields: {
        name: 'MailBox Name',
      },
    },
    parsers: {
      name: 'Parsers',
      deleteDialog: {
        head: 'Delete parser',
        body:
          'This operation will remove parser and it settings. It cannot be undone',
        error: 'Unable to remove parser. Try again later',
      },
      fields: {
        id: 'ID',
        name: 'Parser Name',
        parserData: 'Parser Data',
        tenantName: 'Tenant',
        mailBoxes: 'Mail boxes',
      },
      dialog: {
        identifier: 'Identifier',
        selectIdentifier: 'Select message identifier',
        fieldTransformer: 'Field transformer',
        selectFieldTransformer: 'Select fields to extract',
        dataExpressions: 'Data Expressions',
        selectDataExpressions: 'Define data selectors',
        id: 'Id',
        incident: 'Incident',
        createIncident: 'Create incident',
      },
      validation: {
        noDataExprName: 'Name can not be empty!',
        passwordConfirm: 'Must be the same as the new password',
        regex:
          'Password should contain at least one uppercase, one lowercase, one number, one special character',
      },
      bulkActions: {
        error: "You can't select parsers with different statuses of Enabled",
      },
      dataView: {
        create: 'create data view after submit',
        linkMailBox: 'link to mailBox',
        linkParser: 'link to parser',
        linkHook: 'link to webHook',
        linkSource: 'link to generic source',
        name: 'Name',
      },
    },
    pricingPlan: {
      name: 'Your Plan',
      chargeMonthly: 'WE WILL CHARGE YOUR CREDIT CARD MONTHLY',
      chargeYear: 'WE WILL CHARGE YOUR CREDIT CARD ONCE A YEAR',
      selectPlan: {
        name: 'Select Plan',
      },
      buttons: {
        purchase: 'Purchase',
        blocked: 'Blocked',
        currentPlan: 'Current Plan',
      },
      usage: {
        name: 'Usage',
        currentPlan: 'CURRENT PLAN',
        subscriptionStatus: 'Subscription status',
        plan: 'PLAN',
        thisMonth: 'this month',
        thisYear: 'this year',
        youCanProcess: 'You can receive up to',
        youCanSend: 'You can send up to',
        youCanCreate: 'You can create up to',
        yourCurrentBalance: 'Your current account balance is',
        emails: 'Alerts (Emails and WebHooks)',
        sms: 'SMS',
        nextRest: 'Next rest on the',
        numberOfMailBoxes: 'mailboxes',
        devices: 'uptime monitors',
        dataViews: 'data views',
      },
    },
    billing: {
      name: 'Billing',
      updateButton: 'Update',
      invoices: {
        name: 'Invoices',
        content: {
          header: 'YOUR INVOICES',
          middle:
            'There are no invoices yet. Please upgrade to a paid subscription?',
          text: 'Upcoming invoices are automatically sent to this Email.',
        },
      },
      billingAddress: {
        name: 'Billing Address',
        content: {
          updateAddress: 'UPDATE YOUR BILLING ADDRESS',
          fillName: 'Full Name',
          phone: 'Phone',
          address: 'Address 1',
          city: 'City',
          postCode: 'Zip Code',
        },
      },
      creditCard: {
        name: 'Credit Card',
        content: {
          header: 'UPDATE YOUR CREDIT CARD',
          currentCard: 'Current credit card is',
          cardHolder: 'card holder',
        },
      },
      discount: {
        name: 'Dealer Code',
      },
    },
    chats: {
      name: 'Chats',
      fields: {
        users: 'Users',
        send: 'Send',
        messages: 'Messages',
        id: 'ID',
        name: 'Name',
        createdAt: 'Created At',
        updatedAt: 'Updated At',
      },
      buttons: {
        send: 'Send',
        deleteChat: 'Delete chat',
      },
    },
    messages: {
      deletedMessage: 'Message was deleted.',
      loading: 'Loading...',
    },
    users: {
      name: 'Users',
      fields: {
        isActive: 'Inactive/Active',
        confirmPassword: 'Please confirm your password',
        id: 'ID',
        firstName: 'First Name',
        lastName: 'Last Name',
        email: 'Email',
        updatedAt: 'Updated At',
        roleId: 'Role',
        source: 'Source',
      },
      buttons: {
        removeFromCompany: 'Remove from company',
      },
      deletedUser: 'User was deleted',
      invitation: {
        accessDenied: 'Access denied',
        invitationSent: 'Sign up link sent',
        registrationExpired: 'Sign up link is invalid or expired',
        userAlreadyExist: 'The user with specified email already exists',
        authorizedForSignUp:
          'Please log out then proceed with the registration',
        noSettings:
          'Check for email settings. No permissions for email sending',
      },
    },
    posts: {
      name: 'Posts',
      fields: {
        id: 'ID',
        title: 'Title',
        text: 'Text',
        authorId: 'Author',
        qrData: 'QR Data',
        files: 'Files',
        createdAt: 'Created At',
        updatedAt: 'Updated At',
      },
    },
    generator: {
      name: 'Generators',
    },
    companies: {
      name: 'Companies',
      fields: {
        selectCompany: 'Select company',
        inviteUserEmail: 'Email for user invitation',
      },
      errors: {
        noUserCompanies: 'No companies assigned to user',
      },
      buttons: {
        inviteUser: 'Invite user',
        deleteCompany: 'Delete',
        saveCompany: 'Save',
      },
      notifications: {
        invitationSent: 'Invitation sent',
        accessDenied: 'Access denied',
        invitationInvalid: 'Invitation link is invalid or user not valid',
        userAlreadyInCompany: 'The user already belongs to the company',
        successfulInvitation: 'Accepting invitation was successful',
        unauthorizedForInvitation:
          'Please login with invited user to accept invitation',
        invalidInviteLink: 'Invitation link is invalid',
        noSettings:
          'Check for email settings. No permissions for email sending',
      },
    },
    calendar: {
      name: 'Calendar',
    },
    emailTemplates: {
      name: 'Email Templates',
    },
    settings: {
      name: 'Settings',
    },
    dataSources: {
      name: 'Data sources',
    },
    dataSourcesIntegrations: {
      name: 'Integrations',
    },
    tasks: {
      name: 'Tasks',
    },
    formBuilder: {
      name: 'FormDisplay Builder',
      text: {
        formPreview: 'FormDisplay Preview',
      },
      errors: {
        uniqueFieldKey: 'Input source should be unique across all form inputs.',
        uniqueFieldKeyWhileEditing:
          'Input source can not be the same as other fields source while editing field.',
        illegalFieldSymbols: 'The following symbols are invalid:  / . $ [ ] #',
        illegalFieldBeginning: 'Input source can not start with a number.',
        inputsValueAbsent:
          'Please fill all fields in order to add them to the form preview',
        choicesAbsent: 'Please provide choices for current input.',
        uniqueChoices: 'Choices should be unique across single input.',
        emptyChoices: 'Choices fields can not be empty.',
        invalidValidation:
          'You provide invalid validation. e.g. number and email pattern can not belong to the same field.',
      },
      buttons: {
        addField: 'Add Field',
        editField: 'Edit Field',
      },
      fields: {
        name: 'FormDisplay Name',
        inputType: 'Input Type',
        inputId: 'Input ID',
        inputLabel: 'Input Label',
        choice: 'Choice',
        fieldId: 'Field id',
        multipleSelect: 'Multiple select',
        useTime: 'Use time',
      },
      inputs: {
        textInput: 'Text Input',
        checkboxInput: 'Checkbox',
        numberInput: 'Number Input',
        radioInput: 'Radio Input',
        selectInput: 'Select Input',
        multipleCheckboxInput: 'Multiple Checkbox',
        dateInput: 'Date input',
      },
    },
    dynamicForm: {
      name: 'Dynamic FormDisplay',
      fields: {
        id: 'FormDisplay Id',
      },
    },
    genericSources: {
      name: 'Generic Sources',
      fields: {
        id: 'ID',
        name: 'Name',
        genericPipelineName: 'Generic Pipeline',
        inputData: 'Input Data',
      },
      deleteHeader: 'Are you sure that you want to delete this source?',
      deleteDetails:
        'This action will remove source and all database records. It cannot be undone',
    },
    autotaskWebHooks: {
      name: 'Autotask Web Hooks',
      fields: {
        id: 'ID',
        integrationId: 'Integration ID',
        integrationName: 'Integration Name',
        autotaskHookId: 'Autotask Hook ID',
      },
    },
  },
  ra: {
    registration: {
      fullName: 'Full Name',
      email: 'Email',
      companyName: 'Company Name',
      pwd: 'Password',
      confPwd: 'Password Confirmation',
      continue: 'Continue',
      selectDataCenter: 'SELECT DATA CENTER',
      selectPricingPlan: 'BEST PRICING PLAN FOR YOU',
      cardNumber: 'Invalid card number',
    },
    creditCard: {
      name: 'Name of credit card',
      number: 'Credit card number',
      month: 'Month',
      year: 'Year',
      cvv: 'CVV',
      address: 'Address',
      coupon: 'Dealer Code',
      city: 'City',
      postCode: 'Postal Code',
      billingEmail: 'Billing Email',
      billingFormTitle: 'BILLING INFORMATION',
      billingFormSubTitle:
        'Your subscription is DEMO. Fill billing information to activate regular mode',
      discount: 'Dealer Code',
      selectPlan: 'Select your pricing plan',
    },
    auth: {
      microsoft: 'Microsoft',
      mfaCode: 'MFA Code',
      email: 'Email',
      validation: {
        email: 'Incorrect email',
      },
      checkAuthError:
        "Client doesn't have permission to access the desired data",
      inactiveUserAuthError: "Inactive users don't have permissions to sign in",
      profileIsNotDefined: 'Profile is not defined',
      noEmailForFacebookLogin: 'No email attached to your facebook account',
      signIn: 'Sign In',
      signUp: 'Sign Up',
      login: 'Login',
      signUpSuccessfull: 'Sign Up is successfull!',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      confirmButton: 'Confirm',
      confirmPassword: 'Confirm Password',
      confirmNewPassword: 'Confirm New Password',
      forgotPassword: 'Forgot Password',
      passwordChanged: 'Your Password has been changed!',
      linkForgetPassword:
        'Link for the password changing will be sent to your email',
      resetPassword: 'Password was changed',
      backToLogin: 'Back to the login page',
      registration: 'Registration',
      setPassword: 'Set password',
    },
  },
  pages: {
    firstMailBox: {
      welcome: `Welcome to ${appConfig.projectName}`,
      create: 'CREATE NEW MAILBOX!',
      action: 'First, enter a name for your mailbox, and then click “Next”',
      name: 'Mailbox Name',
      buttonCaption: 'Next',
    },

    pricingPlan: {
      changePlan: 'Are you sure that you want to change your price plan?',
      cannotUndo: 'You cannot undo this action and roll back to lowest plan!',
    },
    login: {
      required: 'Login is required',
      loginText: 'Login',
      loginButton: 'Log In',
      registrationButton: 'Registration',
      forgotPasswordButton: 'Forgot Password',
      linkingDialogHeader:
        'Enter your password from existing account for linking accounts',
      linkAccounts: 'Link accounts',
      closeDialog: 'Close',
      errors: {
        unexpectedError: 'Sorry, something went wrong. Try again later.',
        invalidEmail: 'Invalid email.',
        userDisabled: 'This user is disabled',
        userNotFound: 'Cannot find user.',
        wrongPassword: 'Wrong password.',
        tooManyAttempts:
          'You did a lot of login attempts. Please, try again later.',
        googleLoginFailure:
          'Sorry, something went wrong with login via Google.',
        facebookLoginFailure:
          'Sorry, something went wrong with login via Facebook',
        accountExists: 'You have already logged in from different resource.',
        alreadyLinked: 'The accounts have already linked.',
        invalidCredentials: 'Invalid credentials.',
        credentialsAlreadyInUse: 'Credentials have been already in use.',
        emailAlreadyInUse: 'Email have been already in use.',
        operationIsNotAllowed: 'Linking is not allowed.',
        invalidVerificationCode: 'Invalid verification code.',
        invalidVerificationId: 'Invalid verification id.',
        unexpectedLinkingError: 'Unexpected linking error, we are sorry.',
        popupBlocked: 'Please allow popups and try again this operation again.',
        passwordRequiredForLinking:
          'Seems that you have already been registered' +
          ' with the same email that provider uses. Please enter password to link accounts.',
        confirmPasswordOnDelete:
          'Please confirm your password to delete account.',
      },
      buttons: {
        signInWithGoogle: 'Sign in with Google',
        signInWithFacebook: 'Sign in with Facebook',
      },
    },
    signUp: {
      signUpText: 'Sign Up',
      alreadyHaveAccountButton: 'Already have an account? Log in',
      errors: {
        alreadyInUse: 'Email is already used. Choose another one.',
        invalidEmail: 'Invalid email.',
        operationNotAllowed: 'Registration is not allowed. We are sorry.',
        weakPassword: 'Please use strongest password.',
        unexpectedError: 'Sorry, something went wrong. Try again later.',
        cannotSaveUser: "Can't save user in the database",
        incorrectLinkingAccount:
          'You have selected the wrong account. Please select account that uses same email that you try to register.',
        createCompanyUserInDB: "Can't save user to company",
      },
    },
    profile: {
      yourImage: 'Your image',
      myProfile: 'My profile',
      profile: 'Profile',
      areYouSure: 'Are you sure that you want to delete your profile?',
      cannotUndoAction: 'You can not undo this action!',
      editProfile: 'Edit profile',
      changePassword: 'Change Password',
      deleteSuccess: 'You have successfully deleted account.',
      errors: {
        cannotLoad: 'Cannot load your profile.',
        deleteUser:
          'Cannot delete your account. Please fill confirm password field and try again.',
      },
      buttons: {
        delete: 'Delete profile',
      },
      image: {
        upload: 'Upload new image',
        remove: 'Remove image',
        hint: 'Images help your teammates recognize you',
      },
    },
    forgotPassword: {
      backToLogin: 'Back to login page',
    },
    resetPassword: {
      errorTitle: 'Unable to reset password. Please try again later',
      errors: {
        expiredCode: 'Verify code already expired. Try to reset password again',
        invalidCode:
          'The action code is invalid. This can happen if the code is malformed, expired, or has already been used. Try to reset password again later.',
        unexpectedError: 'Something went wrong. Please try again later.',
      },
    },
    posts: {
      sendEmailButton: 'Send Email',
      sendEmailFailed: 'Send Email failed',
      titles: {
        edit: 'Edit',
        post: 'Post',
      },
      showMore: 'Show more',
      showLess: 'Show less',
    },
    tasks: {
      validationErrors: {
        titleIsRequired: 'Title is required!',
        shortDescriptionIsRequired: 'Short description is required!',
        shortDescriptionTooLong:
          'Short description should contain less then 5 words',
        endDateIsLessThanStartDate:
          'End date must be more or equal than start date',
      },
      titles: {
        show: 'Task "{{title}}"',
        edit: 'Edit Task "{{title}}"',
        create: 'Creating new task',
      },
      statuses: {
        completed: 'Completed',
        pending: 'Pending',
        underDevelopment: 'Under development',
        underReview: 'Under review',
      },
      priorities: {
        low: 'Low',
        high: 'High',
        middle: 'Middle',
      },
      sendEmailButton: 'Send Email',
      tabs: {
        taskInfoLabel: 'Task info',
        commentsTabLabel: 'Comments',
      },
      calendarButton: 'Calendar',
    },
    users: {
      createUserText: 'Create user',
      status: 'Status',
      activeStatus: 'Active',
      inactiveStatus: 'Inactive',
      editTitle: 'Edit User',
    },
    chats: {
      participantsCount: 'One Participant |||| {{smart_count}} participants',
      unreadMessages: 'Unread messages',
      titles: {
        show: 'Chat "{{name}}"',
        edit: 'Edit Chat "{{name}}"',
      },
    },
    generators: {
      generateCSV: 'Generate CSV',
    },
    emailTemplates: {
      titles: {
        list: 'Email Templates',
        edit: 'Edit Email Template "{{name}}"',
        create: 'Create Email Template',
      },
      assignedResources:
        'This template used for {{resources}} resource |||| This template used for {{resources}} resources',
      useVariables: 'You are able to use next variables.',
      forResource: 'For {{resource}} resource:',
      [resourcesWithEmailTemplates.posts]: 'posts',
      [resourcesWithEmailTemplates.tasks]: 'tasks',
      [resourcesWithEmailTemplates.companies]: 'companies',
    },
    settings: {
      defaultRole: {
        roleLabel: 'Default role',
      },
      email: {
        service: 'Service',
        servicePlaceholder: 'Gmail/...',
        accountAddress: 'Account Address',
        accountPassword: 'Account password',
        emailTemplate: 'Email template * ',
      },
      defaultEditTitle: 'Edit Setting "{{name}}"',
    },
    role: {
      permissions: {
        read: 'View',
        allTasksView: 'View all existing tasks',
        myTasksView: 'View only tasks that was created or assigned on user',
        update: 'Edit',
        create: 'Create',
        createGroup: 'Create Group',
        delete: 'Delete',
        comments: 'Comments',
      },
    },
    calendar: {
      assigned: 'Assigned:',
      estimate: 'Estimate:',
      hours: 'hours',
      fullDay: 'Full day',
    },
    companies: {
      titles: {
        show: 'Company "{{name}}"',
        edit: 'Edit Company "{{name}}"',
      },
      createCompany: {
        createUserRole: "Can't create user role",
        createSuperAdminRole: "Can't create super admin role",
        updateCompany: "Can't update company",
        updateUserRole: "Can't update user role",
        removeUserFromCompany: "Can't remove user from company",
      },
      getUsers: "Can't get company users",
      getCompanyUsers: "Can't get company users",
      updateCompanyUsers: "Can't update company users",
      deleteCompany: "Can't delete company",
    },
  },
  components: {
    qrReader: {
      enableReader: 'Enable QR reader',
      disableReader: 'Disable QR reader',
      errors: {
        unexpectedError: 'Unexpected QR reader error occurred.',
      },
    },
    fileUploader: {
      dragAndDropLabel: 'Drag & Drop your files or',
      browse: 'Browse',
      errors: {
        loadFiles: 'Unfortunately something wrong happened with loading files.',
        storage: {
          unknown: 'An unknown error occurred.',
          objectNotFound: 'No object exists at the desired reference.',
          bucketNotFound: 'No bucket is configured for Cloud Storage.',
          projectNotFound: 'No project is configured for Cloud Storage.',
          quotaExceeded:
            'Quota on your Cloud Storage bucket has been exceeded.',
          unauthenticated: 'User is unauthenticated.',
          unauthorized: 'User is not authorized.',
          retryLimitExceeded:
            'The maximum time limit on an operation  has been exceeded.',
          invalidChecksum:
            'File on the client does not match the checksum of the file received by the server. Try uploading again.',
          canceled: 'User canceled the operation.',
          invalidEventName: 'Invalid event name provided.',
          invalidUrl: 'Invalid URL provided.',
        },
      },
      imageUploader: {
        tenant: {
          title: 'Tenant image',
        },
      },
    },
    qrCodeField: {
      printCode: 'Print QR code',
    },
    messaging: {
      forms: {
        sendMessage: {
          messageLabel: 'Messages',
        },
      },
      emailWasSent: 'Email was sent to {{email}}',
    },
    templateEditor: {
      addImage: 'Add image',
      closeDialog: 'Close',
      uploadImageText: 'Select and Drop Image',
      uploadImageWrongTypeText: 'Only Images allowed!',
    },
  },
  validation: {
    success: 'Success',
    exists: 'Already exists',
    threshold: {
      invalid: 'Number cannot be less than 1',
    },
    url: {
      invalid: 'Url is not valid',
      length: 'Max length - 500 symbols',
    },
    deviceName: {
      length: 'Max length - 70 symbols',
    },
    password: {
      length: 'Min length - 8 symbols',
      required: 'Password field is required.',
      requiredCharCombination:
        'Password should have at least one upper case, one lower case letters, one number and one spec char.',
      unmatchedPasswords: 'Passwords do not match.',
    },
    firstName: 'First Name field is required.',
    lastName: 'Last Name field is required.',
    noSetPermissions: 'There are no set any permissions.',
  },
  api: {
    settings: {
      errors: {
        cannotGetDefaultRole: "We are sorry. Sign up isn't available now.",
      },
    },
    common: {
      errors: {
        permissionDenied: 'Cannot load this.',
        unexpectedError: 'Something went wrong. Please try again later.',
        userAlreadyExist: 'User with specified email already exist.',
      },
    },
  },
  common: {
    queryBuilder: {
      addRule: 'Add rule',
      addGroup: 'Add group',
    },
    appBar: {
      changePassword: 'Change password',
      myProfile: 'My profile',
      editProfile: 'Edit profile',
    },
    copied: 'Value copied to clipboard',
    delete: 'Delete',
    register: 'Register',
    refresh: 'Refresh',
    export: 'Export',
    upload: 'Upload',
    create: 'Create',
    update: 'Update',
    apply: 'Apply',
    add: 'Add',
    get: 'Get',
    submit: 'Submit',
    back: 'Back',
    forward: 'Forward',
    next: 'Next',
    cancel: 'Cancel',
    toDefaults: 'Reset to defaults',
    addCommentLabel: 'Add new comment',
    editCommentLabel: 'Edit',
    updatedAtLabel: 'Updated at',
    unresolvedValue: 'Unresolved value',
    deleteDialogHeader: 'Deleting items',
    deleteDialogText:
      'Are you sure to remove these items? This action can not be undone',
  },
  information: {
    mailBox: {
      parser: {
        tabs: {
          identifier: 'Define the rules to identify this type of letter',
          dataExpression:
            'Create regular expressions to parse certain information from the letter',
          fieldTransformers:
            'Define field names and types of parsed values to be stored',
          incidents: 'Create incident after message parsing',
        },
        dataExpressions: {
          occurrences:
            '<b>All occurrences</b> - get data from all of the Source<br/>' +
            '<b>Selected element</b> - get data from selected DOM element only<br/>' +
            '<b>First occurrence</b> - get only first occurrence in Source',
          sourceType: 'The part where data get from',
        },
      },
    },
  },
};

export default lodashMerge(englishMessages, translates);
